import * as jsondiffpatch from 'jsondiffpatch';
import hash from 'hash.js';
import stringifyCanonical from 'json-stable-stringify';
class PatchClient {
    constructor(initialDocument, {
        onPatchRequest = () => { console.error('onPatchRequest not implemented'); },
        onFullDocumentRequest = () => { console.error('onFullDocumentRequest not implemented'); },
    } = {}, log = false) {
        //Validation
        if (typeof onPatchRequest !== 'function') {
            throw new Error('onPatchRequest must be a function');
        }
        if (typeof onFullDocumentRequest !== 'function') {
            throw new Error('onFullDocumentRequest must be a function');
        }
        if (!initialDocument) {
            throw new Error('Initial document is required');
        }
        if (!initialDocument?._version) {
            throw new Error('Document missing version');
        }


        this.currentDocument = initialDocument;
        this.currentVersion = initialDocument?._version || 0;
        this.currentHash = '';
        this.onPatchRequest = onPatchRequest;
        this.onFullDocumentRequest = onFullDocumentRequest;
        this.log = log;

        if (initialDocument) {
            this.updateHash();
        }
    }

    /**
     * Verifies a given patch list. Returns a list starting from the expected version, throws an error if verification fails
     */
    verifyPatches(incomingPatches) {
        // 1. Sort patches by fromVersion to handle out-of-order arrivals
        const sortedPatches = [...incomingPatches].sort((a, b) =>
            a.metadata.fromVersion - b.metadata.fromVersion
        );

        // 2. Find the first patch that starts from the client's current version
        const startIndex = sortedPatches.findIndex(p => p.metadata.fromVersion === this.currentVersion);

        // 3. Handle cases where no directly applicable starting patch is found
        if (startIndex === -1) {
            const hasLaterPatches = sortedPatches.some(p => p.metadata.fromVersion > this.currentVersion);
            if (hasLaterPatches) {
                this.requestCorrectPatches();
                if (this.log)
                console.warn(`PatchClient: Gap detected. Client at v${this.currentVersion}, but received patches starting later.`);
                return false;
            } else {
                if (this.log)
                console.warn(`PatchClient: No applicable patches found. Client at v${this.currentVersion}. All received patches are older or unrelated.`);
                return false;
            }
        }

        // 4. Extract the relevant sequence of patches to process
        const patchesToProcess = sortedPatches.slice(startIndex);

        // 5. Verify the continuity and validity of this sequence
        let expectedFromVersion = this.currentVersion;
        for (const patch of patchesToProcess) {
            if (patch.metadata.fromVersion !== expectedFromVersion) {
                if (this.log)
                console.warn(`PatchClient: Patch sequence gap. Expected patch from v${expectedFromVersion}, but got from v${patch.metadata.fromVersion}.`);
                this.requestCorrectPatches();
                return false;
            }
            // Ensure toVersion is actually progressing
            if (patch.metadata.toVersion <= patch.metadata.fromVersion) {
                if (this.log)
                console.warn(`PatchClient: Invalid patch versioning in sequence (toVersion <= fromVersion). Patch from v${patch.metadata.fromVersion} to v${patch.metadata.toVersion}.`);
                this.requestCorrectPatches();
                return false;
            }
            expectedFromVersion = patch.metadata.toVersion;
        }
        return patchesToProcess;

    }

    /**
     * Applies incoming patches with version and hash verification
     */
    applyPatches(incomingPatches) {
        if (!Array.isArray(incomingPatches)) {
            throw new Error('Patches must be an array');
        }

        // Verify patches
        const patchesToProcess = this.verifyPatches(incomingPatches);
        if (!patchesToProcess) {
            return false;
        }

        // Apply each patch with verification if enabled
        try {
            let tempDocument = JSON.parse(JSON.stringify(this.currentDocument));
            if (this.log)
            console.log('patches', patchesToProcess[0].patch);
            for (const patch of patchesToProcess) {
                tempDocument._version = patch.metadata.toVersion;

                this.verifySinglePatch(tempDocument, patch);
                console.log('applying patch from v', patch.metadata.fromVersion, ' to v', patch.metadata.toVersion);
                jsondiffpatch.patch(tempDocument, patch.patch);

                // Update expected version after each patch
                this.currentVersion = patch.metadata.toVersion;
            }

            // Verify final hash matches
            const finalHash = this.generateHash(tempDocument);
            const expectedHash = patchesToProcess[patchesToProcess.length - 1].metadata.documentHash;

            if (finalHash !== expectedHash) {
                if (this.log)
                console.warn('Hash mismatch after applying patches');
                this.requestFullDocument();
                return false;
            }

            // All checks passed - commit changes
            this.currentDocument = tempDocument;
            this.updateHash();
            return true;

        } catch (error) {
            console.error('Failed to apply patches:', error);
            this.requestFullDocument();
            return false;
        }
    }

    /**
     * Handles receiving a full document update
     */
    receiveFullDocument(newDocument) {
        if (!newDocument || typeof newDocument !== 'object') {
            throw new Error('Invalid document received');
        }

        try {
            // Verify document has version and hash if we're verifying

            if (typeof newDocument._version !== 'number') {
                throw new Error('Document missing version');
            }

            const receivedHash = this.generateHash(newDocument);
            const computedHash = this.generateHash(newDocument);
            if (receivedHash !== computedHash) {
                throw new Error('Document hash mismatch');
            }


            this.currentDocument = newDocument;
            this.currentVersion = newDocument._version || 0;
            this.updateHash();
            return true;

        } catch (error) {
            console.error('Failed to update state with full document:', error);
            return false;
        }
    }

    /**
     * Requests the correct patch sequence from server
     */
    requestCorrectPatches() {
        if (this.log)
        console.log(`Requesting patches from version ${this.currentVersion}`);
        this.onPatchRequest(this.currentVersion);
    }

    /**
     * Requests the full document from server
     */
    requestFullDocument() {
        if (this.log)
        console.log('Requesting full document');
        this.onFullDocumentRequest();
    }

    /**
     * Verifies a single patch application
     */
    verifySinglePatch(document, patch) {
        try {
            const testDocument = JSON.parse(JSON.stringify(document));
            jsondiffpatch.patch(testDocument, patch.patch);
            const testHash = this.generateHash(testDocument);
            if (testHash !== patch.metadata.documentHash) {
                //Request full document and throw an error
                this.requestFullDocument();
                throw new Error('hash mismatch. expected: ' + patch.metadata.documentHash + ' actual: ' + testHash);
            }
        } catch (error) {
            throw new Error(`Patch verification failed: ${error.message}`);
        }
    }

    /**
     * Updates the current hash of the document
     */
    updateHash() {
        this.currentHash = this.generateHash(this.currentDocument);
    }

    /**
     * Generates SHA-256 hash of the document
     */
    generateHash(document) {
        //canonical json deserialisation to ensure consistent ordering
        const str = stringifyCanonical(document);
        const hashHex = hash.sha256().update(str).digest('hex');
        return hashHex;
    }

    // Getters for current state
    getDocument() { return this.currentDocument; }
    getVersion() { return this.currentVersion; }
    getHash() { return this.currentHash; }
}

export default PatchClient;
